import dayjs from 'dayjs';

/**
 * Capitalize first letter of a string
 *
 * @param {string} str - String to capitalize
 * @returns {string} - Capitalized string
 */
export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Generates a random ID.
 *
 * This function generates a random ID by combining the current timestamp in milliseconds
 * and a random number. The timestamp is formatted as a Unix timestamp (milliseconds since the Unix Epoch).
 * The random number is generated using Math.random, converted to a base-36 string, and then the leading '0.' is removed.
 *
 * @returns {string} The generated random ID.
 */
export function generateRandomId() {
  const now = dayjs();

  return `${now.unix()}${Math.random().toString(36).slice(2)}`;
}

/**
 * Checks if a given string is a valid JSON string.
 *
 * @param str - The string to be checked.
 * @returns `true` if the string is a valid JSON string, otherwise `false`.
 */
export const isValidJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

// Function to encode a string to URL format
export function toUrlFormat(str: string): string {
  return encodeURIComponent(str.trim());
}

// Function to decode a URL-encoded string back to a regular string
export function fromUrlFormat(encodedStr: string): string {
  return decodeURIComponent(encodedStr);
}

/**
 * Gets the initials from a provided name (maximum 2 words)
 *
 * @param {string} name - The full name to extract initials from
 * @returns {string} The uppercase initials (maximum 2 characters)
 * @example
 * getNameInitials('John Doe Smith') // returns 'JD'
 * getNameInitials('Jane Doe') // returns 'JD'
 */
export function getNameInitials(name: string): string {
  const MAX_INITIALS = 2;
  return name
    .split(' ')
    .slice(0, MAX_INITIALS) // Only takes first 2 words
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}
